import React, { useEffect, useState } from 'react';
import { Calendar } from 'lucide-react';
import { useClienteAuth } from '../../context/ClienteAuthContext';
import { supabase } from '../../lib/supabase';

type ProductionSample = {
  id: string;
  name: string;
  tipo: string;
  approved_file: string;
  dtf_file?: string;
  delivery_date: string;
  status: string;
  created_at: string;
  updated_at: string;
};

type SampleTask = {
  id: string;
  task_name: string;
  status: string;
  notes: string;
  start_date: string;
  end_date: string;
  cost: number;
  supplier_id: string;
  task_type_id: string;
};

type FilterState = 'all' | 'pending' | 'in_progress' | 'finished' | 'shipped';

const ClienteMuestras: React.FC = () => {
  const { cliente } = useClienteAuth();
  const [items, setItems] = useState<ProductionSample[]>([]);
  const [filteredItems, setFilteredItems] = useState<ProductionSample[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<FilterState>('in_progress');
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());
  const [sampleTasks, setSampleTasks] = useState<Record<string, SampleTask[]>>({});
  const [loadingTasks, setLoadingTasks] = useState<Set<string>>(new Set());

  useEffect(() => {
    const load = async () => {
      if (!cliente) return;
      setLoading(true);
      setError(null);
      
      try {
        const { data, error } = await supabase.rpc('portal_list_samples', {
          p_portal_cliente_id: cliente.id
        });
        
        if (error) throw error;
        
        // Asegurarse de que data sea un array y tenga el formato correcto
        const muestras = Array.isArray(data) ? data : [];
        
        // Verificar que cada muestra tenga el formato correcto
        const muestrasFormateadas = muestras.map(muestra => ({
          id: muestra.id || '',
          name: muestra.name || 'Sin nombre',
          tipo: muestra.tipo || 'Muestra de Producción',
          approved_file: muestra.approved_file || '',
          dtf_file: muestra.dtf_file || '',
          delivery_date: muestra.delivery_date || '',
          status: muestra.status || 'pending',
          created_at: muestra.created_at || new Date().toISOString(),
          updated_at: muestra.updated_at || new Date().toISOString()
        }));
        
        setItems(muestrasFormateadas);
        setFilteredItems(muestrasFormateadas);

    // Log activity
    try {
      await supabase.rpc('log_cliente_activity_simple', {
        p_cliente_id: cliente.id,
        p_action: 'Visualización de muestras',
        p_entity_type: 'samples',
        p_details: `Cliente visualizó ${muestrasFormateadas.length} muestras de producción`,
        p_action_type: 'view'
      });
      console.log('Muestras activity logged successfully');
    } catch (logError) {
      console.error('Error logging muestras activity:', logError);
    }
        
      } catch (e: any) {
        console.error('Error cargando muestras de producción:', e);
        setError(`No se pudieron cargar las muestras de producción: ${e.message}`);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [cliente]);

  useEffect(() => {
    filterItems();
  }, [items, activeFilter]);

  const filterItems = () => {
    let filtered: ProductionSample[];
    switch (activeFilter) {
      case 'pending':
        filtered = items.filter((item: ProductionSample) => 
          item.status === 'pending'
        );
        break;
      case 'in_progress':
        filtered = items.filter((item: ProductionSample) => 
          item.status === 'in_progress'
        );
        break;
      case 'finished':
        filtered = items.filter((item: ProductionSample) => 
          item.status === 'finished'
        );
        break;
      case 'shipped':
        filtered = items.filter((item: ProductionSample) => 
          item.status === 'shipped'
        );
        break;
      default:
        filtered = items;
    }
    setFilteredItems(filtered);
  };

  const toggleCard = (id: string) => {
    const newExpanded = new Set(expandedCards);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
      // Cargar tareas cuando se expande la card
      if (!sampleTasks[id]) {
        loadSampleTasks(id);
      }
    }
    setExpandedCards(newExpanded);
  };

  const loadSampleTasks = async (sampleId: string) => {
    if (sampleTasks[sampleId]) return; // Ya cargadas
    
    setLoadingTasks((prev: Set<string>) => new Set(prev).add(sampleId));
    try {
      const { data, error } = await supabase
        .from('sample_tasks')
        .select('*')
        .eq('sample_id', sampleId);
      
      if (error) throw error;
      
      const tasks = Array.isArray(data) ? data : [];
      setSampleTasks((prev: Record<string, SampleTask[]>) => ({
        ...prev,
        [sampleId]: tasks
      }));
    } catch (e: any) {
      console.error('Error cargando tareas de muestra:', e);
    } finally {
      setLoadingTasks((prev: Set<string>) => {
        const newSet = new Set(prev);
        newSet.delete(sampleId);
        return newSet;
      });
    }
  };

  const downloadFile = async (url: string, filename: string) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Error descargando archivo:', error);
      alert('Error al descargar el archivo. Intenta abrir el enlace en una nueva pestaña.');
      try {
        window.open(url, '_blank', 'noopener,noreferrer');
      } catch (fallbackError) {
        console.error('Error en fallback:', fallbackError);
      }
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'No definida';
    return new Date(dateString).toLocaleDateString('es-AR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return 'No disponible';
    return new Date(dateString).toLocaleString('es-AR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'finished':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'shipped':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusDisplay = (status: string) => {
    switch (status) {
      case 'finished':
        return 'Terminada';
      case 'in_progress':
        return 'En proceso';
      case 'pending':
        return 'Pendiente';
      case 'shipped':
        return 'Enviada';
      default:
        return status;
    }
  };

  const getTaskStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'finished':
      case 'terminado':
      case 'completed':
      case 'completada':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'in_progress':
      case 'en progreso':
      case 'in progress':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'pending':
      case 'pendiente':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'cancelled':
      case 'cancelada':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getTaskStatusText = (status: string) => {
    switch (status.toLowerCase()) {
      case 'finished':
      case 'terminado':
      case 'completed':
      case 'completada':
        return 'Terminado';
      case 'in_progress':
      case 'en progreso':
      case 'in progress':
        return 'En progreso';
      case 'pending':
      case 'pendiente':
        return 'Pendiente';
      case 'cancelled':
      case 'cancelada':
        return 'Cancelada';
      default:
        return status;
    }
  };

  const getFilterCount = (filter: FilterState) => {
    switch (filter) {
      case 'pending':
        return items.filter((item: ProductionSample) => item.status === 'pending').length;
      case 'in_progress':
        return items.filter((item: ProductionSample) => item.status === 'in_progress').length;
      case 'finished':
        return items.filter((item: ProductionSample) => item.status === 'finished').length;
      case 'shipped':
        return items.filter((item: ProductionSample) => item.status === 'shipped').length;
      default:
        return items.length;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando muestras de producción...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error al cargar muestras de producción</h3>
              <p className="mt-1 text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Mis Muestras de Producción</h1>
        <p className="text-gray-600">Seguimiento de todas tus muestras de producción</p>
        <div className="mt-4 flex items-center space-x-4 text-sm text-gray-500">
          <span>Total: <span className="font-semibold text-gray-900">{items.length}</span> muestras</span>
          <span>•</span>
          <span>Última actualización: {new Date().toLocaleString('es-AR')}</span>
        </div>
      </div>

      {/* Filtros */}
      <div className="mb-6">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setActiveFilter('all')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeFilter === 'all'
                ? 'bg-gray-800 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Todas ({items.length})
          </button>
          <button
            onClick={() => setActiveFilter('pending')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeFilter === 'pending'
                ? 'bg-gray-800 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Pendientes ({getFilterCount('pending')})
          </button>
          <button
            onClick={() => setActiveFilter('in_progress')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeFilter === 'in_progress'
                ? 'bg-gray-800 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            En proceso ({getFilterCount('in_progress')})
          </button>
          <button
            onClick={() => setActiveFilter('finished')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeFilter === 'finished'
                ? 'bg-gray-800 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Terminadas ({getFilterCount('finished')})
          </button>
          <button
            onClick={() => setActiveFilter('shipped')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeFilter === 'shipped'
                ? 'bg-gray-800 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Enviadas ({getFilterCount('shipped')})
          </button>
        </div>
      </div>

      {filteredItems.length === 0 ? (
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No hay muestras de producción</h3>
          <p className="mt-1 text-sm text-gray-500">
            {activeFilter === 'all' 
              ? 'No tienes muestras de producción asignadas.'
              : `No hay muestras en estado "${activeFilter === 'pending' ? 'Pendientes' : activeFilter === 'in_progress' ? 'En proceso' : activeFilter === 'finished' ? 'Terminadas' : 'Enviadas'}"`
            }
          </p>
        </div>
      ) : (
        <div className="grid gap-6">
          {filteredItems.map((muestra) => (
            <div key={muestra.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow duration-200">
              {/* Header de la muestra - Siempre visible */}
              <div 
                className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white cursor-pointer"
                onClick={() => toggleCard(muestra.id)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <h3 className="text-xl font-semibold text-gray-900">{muestra.name}</h3>
                      <svg 
                        className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${
                          expandedCards.has(muestra.id) ? 'rotate-180' : ''
                        }`}
                        fill="none" 
                        viewBox="0 0 24 24" 
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs sm:text-sm mt-2 text-gray-600">
                      <span className="text-gray-500">Creada: {formatDateTime(muestra.created_at)}</span>
                      {muestra.delivery_date && (
                        <>
                          <span className="text-gray-300">•</span>
                          <span className="inline-flex items-center px-2 py-1 rounded-full border text-xs sm:text-sm bg-black text-white border-black">
                            <Calendar className="h-3.5 w-3.5 mr-1" />
                            Entrega: <span className="ml-1 font-semibold">{formatDate(muestra.delivery_date)}</span>
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className={`px-3 py-1 text-xs font-medium rounded-full border ${getStatusColor(muestra.status)}`}>
                      {getStatusDisplay(muestra.status)}
                    </span>
                    <span className="px-3 py-1 text-xs font-medium rounded-full border bg-orange-100 text-orange-800 border-orange-200">
                      {muestra.tipo}
                    </span>
                  </div>
                </div>
              </div>

              {/* Contenido expandible */}
              {expandedCards.has(muestra.id) && (
                <>
                  {/* Contenido principal */}
                  <div className="px-6 py-4">
                    <div className="space-y-8">
                      {/* Progreso de Tareas de Producción */}
                      {loadingTasks.has(muestra.id) ? (
                        <div className="mt-8">
                          <h4 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Progreso</h4>
                          <div className="flex items-center justify-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                            <span className="ml-2 text-gray-600">Cargando tareas...</span>
                          </div>
                        </div>
                      ) : sampleTasks[muestra.id] && sampleTasks[muestra.id].length > 0 ? (
                        <div className="mt-8 space-y-6">
                          {/* Barra de progreso general */}
                          <div>
                            <div className="flex items-center justify-between mb-2 text-xs text-gray-600">
                              <span>Avance</span>
                              <span>
                                {Math.round(
                                  (sampleTasks[muestra.id].filter(t => ['finished','terminado','completed','completada'].includes(String(t.status).toLowerCase())).length /
                                    Math.max(1, sampleTasks[muestra.id].length)) * 100
                                )}%
                              </span>
                            </div>
                            <div className="w-full bg-gray-200 h-2 rounded-full">
                              <div
                                className="h-2 bg-black rounded-full transition-all"
                                style={{
                                  width: `${Math.min(100, Math.round((sampleTasks[muestra.id].filter(t => ['finished','terminado','completed','completada'].includes(String(t.status).toLowerCase())).length / Math.max(1, sampleTasks[muestra.id].length)) * 100))}%`
                                }}
                              />
                            </div>
                          </div>

                          {/* Vertical mobile */}
                          <div className="md:hidden relative pl-6">
                            <div className="absolute left-2 top-0 bottom-0 w-0.5 bg-gray-200" />
                            <div className="space-y-5">
                              {sampleTasks[muestra.id].map((task, index) => (
                                <div key={task.id} className="relative">
                                  <div className={`absolute -left-3 top-0 w-6 h-6 rounded-full bg-white border flex items-center justify-center ${
                                    ['finished','terminado','completed','completada'].includes(String(task.status).toLowerCase())
                                      ? 'border-green-600 text-green-600'
                                      : ['in_progress','en progreso','in progress'].includes(String(task.status).toLowerCase())
                                      ? 'border-yellow-600 text-yellow-600'
                                      : 'border-blue-600 text-blue-600'
                                  }`}>
                                    <span className="text-[10px] font-semibold">{index + 1}</span>
                                  </div>
                                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                                    <div className="flex items-center justify-between">
                                      <h5 className="text-sm font-medium text-gray-900">{task.task_name}</h5>
                                      <span className={`px-2 py-0.5 text-[11px] rounded-full border ${getTaskStatusColor(task.status)}`}>{getTaskStatusText(task.status)}</span>
                                    </div>
                                    <div className="mt-1 grid grid-cols-2 gap-2 text-xs text-gray-600">
                                      {(task.start_date && task.start_date !== 'No disponible' && task.start_date !== 'No definida') && (
                                        <span>Inicio: {formatDate(task.start_date)}</span>
                                      )}
                                      {(task.end_date && task.end_date !== 'No disponible' && task.end_date !== 'No definida') && (
                                        <span>Fin: {formatDate(task.end_date)}</span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Grid desktop */}
                          <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-6">
                            {sampleTasks[muestra.id].map((task, index) => (
                              <div key={task.id} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                                <div className="flex items-center justify-between mb-2">
                                  <div className={`w-8 h-8 rounded-full bg-white border flex items-center justify-center text-xs font-semibold ${
                                    ['finished','terminado','completed','completada'].includes(String(task.status).toLowerCase())
                                      ? 'border-green-600 text-green-600'
                                      : ['in_progress','en progreso','in progress'].includes(String(task.status).toLowerCase())
                                      ? 'border-yellow-600 text-yellow-600'
                                      : 'border-blue-600 text-blue-600'
                                  }`}>{index + 1}</div>
                                  <span className={`px-2 py-0.5 text-[11px] rounded-full border ${getTaskStatusColor(task.status)}`}>{getTaskStatusText(task.status)}</span>
                                </div>
                                <h5 className="text-sm font-medium text-gray-900 mb-1 line-clamp-2">{task.task_name}</h5>
                                <div className="text-xs text-gray-600 space-y-1">
                                  {(task.start_date && task.start_date !== 'No disponible' && task.start_date !== 'No definida') && (
                                    <div>Inicio: {formatDate(task.start_date)}</div>
                                  )}
                                  {(task.end_date && task.end_date !== 'No disponible' && task.end_date !== 'No definida') && (
                                    <div>Fin: {formatDate(task.end_date)}</div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : null}

                      {/* Archivos disponibles */}
                      <div className="mt-8">
                        <h4 className="text-lg font-medium text-gray-900 mb-4">Archivos disponibles</h4>
                        <div className="space-y-3">
                          {/* Ficha aprobada */}
                          {muestra.approved_file && (
                            <div className="flex items-center justify-between p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                              <div className="flex items-center">
                                <div className="flex-shrink-0">
                                  <svg className="h-5 w-5 text-emerald-600" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                  </svg>
                                </div>
                                <div className="ml-3">
                                  <p className="text-sm font-medium text-emerald-900">Ficha Aprobada</p>
                                  <p className="text-xs text-emerald-700">Documento aprobado y listo</p>
                                </div>
                              </div>
                              <button
                                onClick={() => downloadFile(muestra.approved_file, `ficha_aprobada_${muestra.name}.pdf`)}
                                className="px-3 py-1 text-xs font-medium text-emerald-700 bg-emerald-100 rounded-md hover:bg-emerald-200 transition-colors"
                              >
                                Descargar
                              </button>
                            </div>
                          )}

                          {/* Archivo DTF */}
                          {muestra.dtf_file && (
                            <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
                              <div className="flex items-center">
                                <div className="flex-shrink-0">
                                  <svg className="h-5 w-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                  </svg>
                                </div>
                                <div className="ml-3">
                                  <p className="text-sm font-medium text-blue-900">Archivo DTF</p>
                                  <p className="text-xs text-blue-700">Archivo de transferencia digital</p>
                                </div>
                              </div>
                              <button
                                onClick={() => downloadFile(muestra.dtf_file!, `archivo_dtf_${muestra.name}.pdf`)}
                                className="px-3 py-1 text-xs font-medium text-blue-700 bg-blue-100 rounded-md hover:bg-blue-200 transition-colors"
                              >
                                Descargar
                              </button>
                            </div>
                          )}

                          {/* Mostrar mensaje si no hay archivos */}
                          {!muestra.approved_file && !muestra.dtf_file && (
                            <div className="text-center py-6 text-gray-500">
                              <svg className="mx-auto h-8 w-8 text-gray-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              <p className="text-sm">No hay archivos disponibles aún</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Footer con información adicional */}
                  <div className="px-6 py-3 bg-gray-50 border-t border-gray-100">
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>ID: {muestra.id}</span>
                      <span>Última actualización: {formatDateTime(muestra.updated_at)}</span>
                    </div>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ClienteMuestras;
